import React, { useEffect, useRef, useState } from 'react';
import Layout from '../../components/shared/Layout';
import { Card, PageHeader, Button, Input } from '../../components/shared/ui';
import { companyAPI } from '../../utils/api';
import api from '../../utils/api';

const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:5001/api').replace('/api', '');
const SECTORS = ['Siber Güvenlik','Bilgi Teknolojileri','Savunma & Güvenlik','Finans & Bankacılık','Telekomünikasyon','Danışmanlık','Kamu','Diğer'];
const EMP_COUNTS = ['1-10','10-50','50-200','200-500','500+'];

const Field = ({ label, children }) => (
  <div>
    <label className="block text-sm font-medium text-ink-700 mb-1">{label}</label>
    {children}
  </div>
);

const CompanyProfile = () => {
  const [profile, setProfile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const fileRef = useRef();

  useEffect(() => { companyAPI.getProfile().then(r => setProfile(r.data)); }, []);
  const handleChange = (f, v) => setProfile(p => ({ ...p, [f]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await companyAPI.updateProfile(profile);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } finally { setSaving(false); }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLogoUploading(true);
    try {
      const form = new FormData();
      form.append('logo', file);
      const r = await api.post('/companies/logo', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      setProfile(p => ({ ...p, logo_url: r.data.logo_url }));
    } catch { alert('Logo yüklenemedi'); }
    finally { setLogoUploading(false); }
  };

  const handleLogoDelete = async () => {
    if (!window.confirm('Logonuz kaldırılacak. Emin misiniz?')) return;
    try {
      await companyAPI.deleteLogo();
      setProfile(p => ({ ...p, logo_url: null }));
    } catch { alert('Logo kaldırılamadı'); }
  };

  if (!profile) return <Layout><p className="text-ink-500">Yükleniyor...</p></Layout>;

  return (
    <Layout>
      <PageHeader
        title="Şirket Profili"
        description="Şirketinizin bilgilerini güncelleyin"
        actions={
          <Button onClick={handleSave} disabled={saving}>
            {saved ? '✓ Kaydedildi' : saving ? 'Kaydediliyor...' : 'Kaydet'}
          </Button>
        }
      />

      <div className="max-w-3xl space-y-5">
        <Card>
          <h2 className="text-base font-semibold text-ink-900 mb-4">Şirket Logosu</h2>
          <div className="flex items-center gap-5">
            <div className="w-20 h-20 rounded-xl bg-ink-100 border border-ink-200 flex items-center justify-center overflow-hidden flex-shrink-0">
              {profile.logo_url
                ? <img src={`${API_BASE}${profile.logo_url}`} alt="logo" className="w-full h-full object-cover" />
                : <svg className="w-8 h-8 text-ink-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M3 21V7l9-4 9 4v14M9 21V12h6v9"/></svg>}
            </div>
            <div>
              <div className="flex flex-wrap gap-2">
                <Button variant="secondary" onClick={() => fileRef.current.click()} disabled={logoUploading}>
                  {logoUploading ? 'Yükleniyor...' : (profile.logo_url ? 'Logoyu Değiştir' : 'Logo Yükle')}
                </Button>
                {profile.logo_url && (
                  <button type="button" onClick={handleLogoDelete}
                    className="px-3 py-1.5 text-sm font-medium text-red-600 border border-red-200 rounded-md hover:bg-red-50">
                    Logoyu Kaldır
                  </button>
                )}
              </div>
              <p className="text-xs text-ink-400 mt-1.5">PNG, JPG — maks 2 MB</p>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} />
            </div>
          </div>
        </Card>

        <Card>
          <h2 className="text-base font-semibold text-ink-900 mb-4">Temel Bilgiler</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <Input label="Şirket Adı" value={profile.name || ''} onChange={e => handleChange('name', e.target.value)} />
            </div>
            <Field label="Sektör">
              <select className="input" value={profile.sector || ''} onChange={e => handleChange('sector', e.target.value)}>
                <option value="">Seçin</option>
                {SECTORS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="Çalışan Sayısı">
              <select className="input" value={profile.employee_count || ''} onChange={e => handleChange('employee_count', e.target.value)}>
                <option value="">Seçin</option>
                {EMP_COUNTS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Input label="Şehir" value={profile.city || ''} onChange={e => handleChange('city', e.target.value)} placeholder="İstanbul" />
            <Input label="Web Sitesi" value={profile.website || ''} onChange={e => handleChange('website', e.target.value)} placeholder="https://sirket.com" />
          </div>
        </Card>

        <Card>
          <h2 className="text-base font-semibold text-ink-900 mb-2">Şirket Hakkında</h2>
          <p className="text-sm text-ink-500 mb-3">Öğrencilerin görüneceği tanıtım metni. Odak alanlarınız, çalışma kültürünüz, staj programınız hakkında bilgi verin.</p>
          <textarea rows={6} className="input resize-y"
            value={profile.description || ''}
            onChange={e => handleChange('description', e.target.value)}
            placeholder="Şirketinizi ve staj programınızı tanıtın..." />
        </Card>

        {!profile.is_approved && (
          <Card className="border-amber-200 bg-amber-50">
            <p className="text-sm text-amber-900">Hesabınız admin onayı bekliyor. Onaylandıktan sonra ilan oluşturabilirsiniz.</p>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default CompanyProfile;
